import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { userAppContext } from '../../Context/Context';
import getApi from '../../services/GetApi';
import Alert from '../../global/Alert/Alert';
import LoadingSpinner from '../../global/Loader/LoaderSpinner';
import EnrichmentModal from './components/EnrichmentModal';
import ValidationMessage from './components/ValidationMessage';
import { createEnrichmentInfoContent, getEnrichmentPayload } from '../../Components/AdvancedSearch/helpers';
import { getItemCount } from '../../Components/AdvancedSearch/AdvancedSearchHelpers';
import { UserContext } from '../../types';
import { advancedExamples } from '../../Components/AdvancedSearch/AdvancedExamples';
import JSZip from 'jszip';
import {
  detectIdentifierType,
  validateIdentifierList,
  validateTypeConsistency,
} from '../../utils/inputValidations';
import useFetchSuggestions from '../../hooks/useFetchSuggestions';
import EnrichmentSwarmPlot from './components/EnrichmentGraph';
import { Canvg } from 'canvg';
import { CombinedValidation, EnrichmentResult } from './types';
import { geneFilterOptions, tabs } from './helpers';
import { trackEnrichmentAction } from '../../utils/analytics';

const Enrichment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enrichmentFrom, clearEnrichmentFrom } = useEnrichmentState();
  const { user, authorization, isAuthenticated } = userAppContext() as unknown as UserContext;

  const initialData = location.state || {};

  const [searchType, setSearchType] = useState(initialData.searchType || 'GENES');
  const [text, setText] = useState(initialData.text || '');
  const [geneFilterType, setGeneFilterType] = useState(
    initialData.selectedFilter || initialData.geneFilterType || 'geneSymbol',
  );

  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showApiKeyAlert, setShowApiKeyAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState<EnrichmentResult[] | null>(null);

  const [validation, setValidation] = useState<CombinedValidation | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const [csvData, setCsvData] = useState<string | null>(null);
  const [metadataContent, setMetadataContent] = useState<string | null>(null);
  const graphRef = useRef<HTMLDivElement | null>(null);
  const swarmRef = useRef<HTMLDivElement | null>(null);

  const searchTypeRef = useRef(searchType);

  const isAcademic = user?.['cognito:groups'] ? user?.['cognito:groups'][0] === 'ROLE_ACADEMIC' : null;

  const [enrichmentParams, setEnrichmentParams] = useState({
    commonVariants: 2,
    commonGenes: 2,
    maxPvalue: 0.1,
    maxScore: 1,
    minScore: 0,
    pageNumber: 0,
    source: isAcademic ? 'CURATED' : 'ALL',
  });

  const { notFoundItems, fetchSuggestions } = useFetchSuggestions(
    searchType,
    searchTypeRef.current === 'GENES' ? geneFilterType : 'strID',
    authorization,
    isAuthenticated,
  );

  useEffect(() => {
    if (initialData.selectedFilter) {
      setGeneFilterType(initialData.selectedFilter);
    }
  }, [initialData.selectedFilter]);

  useEffect(() => {
    searchTypeRef.current = searchType;
    if (searchTypeRef.current === 'GENES') setGeneFilterType('geneSymbol');
  }, [searchType]);

  useEffect(() => {
    if (text.trim()) {
      validateInput();
    } else {
      setValidation(null);
      setShowValidation(false);
    }
  }, [text, searchType, geneFilterType]);

  useEffect(() => {
    if (enrichmentResults && graphRef.current) {
      setTimeout(() => {
        graphRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [enrichmentResults]);

  const validateInput = () => {
    if (!text.trim()) {
      setValidation(null);
      return;
    }

    const expectedType = searchType === 'VARIANTS' ? 'strID' : geneFilterType;

    const identifierValidation = validateIdentifierList(text, expectedType);

    const consistencyValidation = validateTypeConsistency(text, geneFilterType, searchType);

    const combinedValidation = {
      ...identifierValidation,
      ...consistencyValidation,
      isValid: identifierValidation.isValid && consistencyValidation.isConsistent,
    };

    setValidation(combinedValidation);
    setShowValidation(true);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lines = value.split('\n');

    if (lines.length > 4000) {
      const limitedValue = lines.slice(0, 4000).join('\n');
      setText(limitedValue);
    } else {
      setText(value);
    }
  };

  const clearInputText = () => {
    setText('');
    setValidation(null);
    setShowValidation(false);
    setEnrichmentResults(null);
  };

  const showExamples = () => {
    const formatFilterType =
      searchTypeRef.current === 'GENES'
        ? geneFilterType.replace('Id', 'ID').replace('geneSymbol', 'symbol')
        : 'strID';

    const exampleItems = advancedExamples[searchTypeRef.current]?.[formatFilterType] || [];
    setText(exampleItems.join('\n'));
  };

  const handleAutoDetectSuggestion = () => {
    if (validation?.detectedType && searchType === 'GENES') {
      setGeneFilterType(validation.detectedType);
    }
  };

  const handleSearchTypeChange = newSearchType => {
    setSearchType(newSearchType);

    if (newSearchType === 'VARIANTS' && text.trim()) {
      const detectedType = detectIdentifierType(text);
      if (detectedType && detectedType !== 'strID') {
        // Text will be validated in the useEffect
      }
    }
  };

  const getEnrichment = useCallback(
    async (customParams = enrichmentParams) => {
      const formatFilterType =
        searchTypeRef.current === 'GENES' ? geneFilterType.replace('geneSymbol', 'symbol') : 'strID';

      if (!text.trim()) {
        alert('Please enter some data before running enrichment');
        return;
      }

      if (validation && !validation.isValid) {
        alert('Please fix the validation errors before running enrichment');
        return;
      }

      setShowErrorAlert(false);
      setShowSuccessAlert(false);
      setShowApiKeyAlert(false);
      setEnrichmentLoading(true);
      setEnrichmentResults(null);

      const apiUrl = `${process.env.REACT_APP_URL_API_API}api/v1`;
      const payload = getEnrichmentPayload(searchType, text, customParams, formatFilterType);

      const username = user?.['cognito:username'] || user?.username || user?.userName || null;
      if (!username) {
        setShowApiKeyAlert(true);
        setEnrichmentLoading(false);
        return;
      }

      try {
        const apiKey = await getApi(username);
        const baseHeaders = {
          'Content-Type': 'application/json',
          Authorization: apiKey?.payload?.api_key || apiKey || '',
        };

        const endpoint = `${apiUrl}/enrichment/${searchType === 'GENES' ? 'gene' : 'variant'}`;

        const csvHeaders = { ...baseHeaders, Accept: 'application/csv' };
        const csvResponse = await fetch(endpoint, {
          method: 'POST',
          headers: csvHeaders,
          body: JSON.stringify(payload),
        });

        const jsonHeaders = { ...baseHeaders, Accept: 'application/json' };
        const jsonResponse = await fetch(endpoint, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify(payload),
        });

        if (csvResponse.status === 200 && jsonResponse.status === 200) {
          const csvText = await csvResponse.text();
          const jsonData = await jsonResponse.json();

          const lines = csvText.trim().split('\n');
          const hasData = lines.length > 1 && lines.some(line => line.trim().length > 0);

          if (!hasData || !jsonData.payload || jsonData.payload.length === 0) {
            setShowWarningAlert(true);
            setEnrichmentLoading(false);
            return;
          }

          setEnrichmentResults(jsonData.payload);

          // CSV content
          const infoContent = createEnrichmentInfoContent({
            searchType,
            text,
            enrichmentParams: customParams,
            geneFilterType,
          });

          setCsvData(csvText);
          setMetadataContent(infoContent);
          setShowSuccessAlert(true);
        } else if (csvResponse.status === 401 || jsonResponse.status === 401) {
          setShowApiKeyAlert(true);
        } else {
          setShowErrorAlert(true);
        }
      } catch (error) {
        console.error('Error running enrichment:', error);
        setShowErrorAlert(true);
      } finally {
        setEnrichmentLoading(false);
      }
    },
    [user, searchType, text, enrichmentParams, geneFilterType, validation],
  );

  const handleModalSubmit = params => {
    setEnrichmentParams(params);
    setShowModal(false);
    getEnrichment(params);
  };

  const goBack = () => {
    if (enrichmentFrom?.from === 'advancedSearch') {
      const { searchType, selectedFilter, text } = enrichmentFrom;

      navigate('/advancedSearch', {
        state: { searchType, selectedFilter, text },
      });
    } else {
      navigate('/');
    }

    clearEnrichmentFrom();
  };

  const handleEnrichmentClick = async () => {
    trackEnrichmentAction('run_enrichment_click', 'enrichment_page');

    if (validation && !validation.isValid) {
      alert('Please fix the validation errors before running enrichment');
      return;
    }
    const inputs = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line);
    await fetchSuggestions(inputs);
    setShowModal(true);
  };

  const handleDownloadResults = async () => {
    if (!csvData || !metadataContent) {
      alert('No data available to download');
      return;
    }

    try {
      const zip = new JSZip();
      zip.file(`enrichment_${searchType.toLowerCase()}_result.csv`, csvData);
      zip.file(`enrichment_${searchType.toLowerCase()}_metadata.txt`, metadataContent);

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enrichment_${searchType.toLowerCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading results:', error);
      setShowErrorAlert(true);
    }
  };

  const handleDownloadGraph = async () => {
    try {
      if (!swarmRef.current) {
        alert('Graph not found');
        return;
      }

      const svgEl = swarmRef.current.querySelector('[data-nivo="swarmplot"] svg');
      if (!svgEl) {
        alert('Graph SVG not found');
        return;
      }

      const clonedSvg = svgEl.cloneNode(true) as SVGElement;

      const rect = svgEl.getBoundingClientRect();
      const width = rect.width || 800;
      const height = rect.height || 600;

      clonedSvg.setAttribute('width', width.toString());
      clonedSvg.setAttribute('height', height.toString());

      const svgString = new XMLSerializer().serializeToString(clonedSvg);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        alert('Canvas context not available');
        return;
      }

      const scale = 2;
      canvas.width = width * scale;
      canvas.height = height * scale;
      ctx.scale(scale, scale);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      const v = Canvg.fromString(ctx, svgString);
      await v.render();

      canvas.toBlob(blob => {
        if (!blob) {
          alert('Failed to generate image');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'enrichment_graph.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Error generating PNG:', error);
      alert('Error generating PNG. Please try again or contact support.');
    }
  };

  const canRunEnrichment = text.trim() && (!validation || validation.isValid);

  return (
    <div className="container mx-auto px-4 py-8">
      {enrichmentLoading && <LoadingSpinner style={{ position: 'fixes' }} />}

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Disease Enrichment Analysis</h1>
        <p className="text-gray-600 mb-6">
          Perform enrichment analysis on your gene or variant lists to identify significant associations of
          diseases and phenotypes.
        </p>
      </div>

      <div className="multiple-filters">
        {/* Search Type Selection */}
        <div className="mb-6">
          <div className="flex gap-4 justify-center">
            {tabs.map(type => (
              <label key={type} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="searchType"
                  value={type}
                  checked={searchType === type}
                  onChange={e => handleSearchTypeChange(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium">{type.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Gene/Variant identifier dropdown */}
        {searchType === 'GENES' ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier type</label>
            <select
              value={geneFilterType}
              onChange={e => setGeneFilterType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              {geneFilterOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Add your search list (separate items by line):</p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Variant identifier</label>
            <input
              readOnly
              value="rs number"
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">Add your search list (separate items by line):</p>
          </div>
        )}

        {/* Text Input Area */}
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add your search list (separate items by line):
          </label>
          <textarea
            placeholder={`Enter ${searchType.toLowerCase()} (one per line)`}
            value={text}
            onChange={handleQueryChange}
            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              validation && !validation.isValid ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            rows={8}
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-sm text-gray-500">Items: {getItemCount(text)}</p>
          </div>
        </div>

        {/* Validation Messages */}
        {showValidation && (
          <ValidationMessage validation={validation} onSuggestionClick={handleAutoDetectSuggestion} />
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap mb-6">
          <button onClick={goBack} className="filter-upload">
            Back
          </button>
          <button
            onClick={handleEnrichmentClick}
            disabled={enrichmentLoading || !canRunEnrichment}
            className={`filter-upload ${!canRunEnrichment ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {enrichmentLoading ? 'Running...' : 'Run Enrichment'}
          </button>

          <button onClick={clearInputText} className="filter-upload">
            Clear List
          </button>

          <button onClick={showExamples} className="filter-upload">
            Show Examples
          </button>
          {enrichmentResults && csvData && (
            <button onClick={handleDownloadResults} className="filter-upload">
              Download Results (ZIP)
            </button>
          )}
        </div>
      </div>

      {/* Enrichment Modal */}
      {showModal && (
        <EnrichmentModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialParams={enrichmentParams}
          searchType={searchType}
          notFoundItems={notFoundItems}
          user={user}
        />
      )}

      {/* Alerts */}
      {showErrorAlert && (
        <Alert
          message={`There was an error running the ${searchType.toLowerCase()} enrichment analysis.`}
          type="error"
        />
      )}

      {showWarningAlert && (
        <Alert
          message="There are no data available for this enrichment analysis with the provided identifiers."
          type="warning"
        />
      )}

      {showSuccessAlert && <Alert message="Enrichment analysis completed successfully." type="success" />}

      {showApiKeyAlert && (
        <Alert message="You need to set your API key." type="warning">
          <span
            onClick={() => navigate('/Profile-area#apiKey')}
            className="font-bold ml-2 cursor-pointer hover:text-blue-600"
          >
            Set API Key
          </span>
        </Alert>
      )}

      {/* Enrichment Results Visualization */}
      <div ref={graphRef}>
        {enrichmentResults && (
          <EnrichmentSwarmPlot ref={swarmRef} data={enrichmentResults} onDownload={handleDownloadGraph} />
        )}
      </div>
    </div>
  );
};

export default Enrichment;
